/**
 * Two-Factor Authentication API Client
 *
 * ISO 27001 A.9.4.2 - Secure log-on procedures
 */

import { safeFetch } from '@/utils/api';

export interface TwoFactorStatus {
  enabled: boolean;
  methods: {
    type: 'TOTP' | 'SMS' | 'EMAIL';
    isPrimary: boolean;
    isVerified: boolean;
    lastUsedAt: string | null;
  }[];
  backupCodesRemaining?: number;
}

export interface TOTPSetupResult {
  qrCode: string;
  secret: string;
  backupCodes: string[];
  methodId: string;
}

export interface VerifyResult {
  success: boolean;
  message: string;
}

const API_BASE = '/api/v1/core/auth/2fa';

export const twoFactorApi = {
  /**
   * Get current 2FA status
   */
  async getStatus(): Promise<TwoFactorStatus> {
    const response = await safeFetch(`${API_BASE}/status`);
    const result = await response.json();
    return result.data;
  },

  /**
   * Start TOTP setup - returns QR code and backup codes
   */
  async setupTOTP(): Promise<TOTPSetupResult> {
    const response = await safeFetch(`${API_BASE}/totp/setup`, {
      method: 'POST',
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Verify TOTP code during setup to activate 2FA
   */
  async verifyTOTPSetup(code: string): Promise<VerifyResult> {
    const response = await safeFetch(`${API_BASE}/totp/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const result = await response.json();
    return result.data || result;
  },

  /**
   * Disable TOTP 2FA (requires current TOTP code)
   */
  async disableTOTP(code: string): Promise<VerifyResult> {
    const response = await safeFetch(`${API_BASE}/totp`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const result = await response.json();
    return result.data || result;
  },

  /**
   * Regenerate backup codes (requires current TOTP code)
   */
  async regenerateBackupCodes(code: string): Promise<string[]> {
    const response = await safeFetch(`${API_BASE}/backup/regenerate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    });
    const result = await response.json();
    return result.data;
  },

  /**
   * Verify 2FA during login (with challenge token)
   */
  async verify2FA(challengeToken: string, code: string, useBackup: boolean = false): Promise<{
    success: boolean;
    accessToken?: string;
    refreshToken?: string;
    user?: any;
    message?: string;
  }> {
    const response = await safeFetch(`${API_BASE}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ challengeToken, code, useBackup }),
    });
    const result = await response.json();
    return result;
  },
};

export default twoFactorApi;
